import React, { Fragment, useState, useEffect, useRef, useMemo, } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, } from 'reactstrap';
import numeral from 'numeral';
import { findKey, fromPairs, mapValues, omit, pickBy, mergeWith, last, chunk, uniqBy, orderBy, uniq, isEqual, get, sumBy, isEmpty, isNumber as _isNumber, groupBy, sortBy, keyBy, range, debounce, pick, } from 'lodash';
import { toast } from 'react-toastify';
import classnames from 'classnames';
import { Container, Draggable } from 'react-smooth-dnd';
import fileDownload from 'js-file-download';
import { unparse as unparseCsv, parse as parseCsv } from 'papaparse';
import { TextEncoder, TextDecoder } from 'text-encoding';
import qs from 'qs';
import Select from 'react-select';
import { useToggle, useDebounce, useMap, } from 'react-use';
import { useLocation, useHistory } from 'react-router-dom';
import { format as formatDate, startOfMonth, endOfMonth, addYears, addMonths, } from 'date-fns';
import arrayMove from 'array-move';

import firebase, { functions } from '../firebase';
import { mapKeysToJa, mapKeysFromJa, jas, } from '../shared/texts';
import { fiscalYearOfPeriod, } from '../utils';
import { fields } from '../shared/models/budgetContainer';
import { plans, canUseForecast } from '../shared/models/company';
import { manualValueSuffixes, saveManualForecastValues } from '../shared/models/customAccountItem';
import { batch, getCollectionData, getAllCollectionDataByChunk, } from '../shared/firebase';
import { allDimensions, documentTypes, budgetSubjectTypes, } from '../shared/config';
import useCollectionSubscription from './hooks/useCollectionSubscription';
import useUser from './hooks/useUser';
import ProgressButton from './ProgressButton';
import ModelFormModal from './modals/ModelFormModal';
import AddButton from './AddButton';
import EditButton from './EditButton';
import DeleteButton from './DeleteButton';
import ExportButton from './ExportButton';
import ImportButton from './ImportButton';

const { entries } = Object;
const db = firebase.firestore();

export default function BudgetButton (props) {
  const { company, period, accountItems, customAccountItems = [], onChangeBudgets, } = props;
  const maxContainersCount = Math.max(...(company.enabledPlans || []).map(_ => plans[_]?.maxBudgetContainersCount || 0), 1);
  // NOTE: 手入力カスタム科目は budgets ではなくカスタム科目ドキュメント側に値を持つため、見込インポートでは別扱いにする
  const manualCustomAccountItems = customAccountItems.filter(_ => _.isManual);
  const manualCustomAccountItemsByName = keyBy(manualCustomAccountItems, 'name');
  const accountItemNames = accountItems.map(_ => _.name);
  const user = useUser();
  const { start_date: startDate, } = fiscalYearOfPeriod(period, company.fiscalYears);
  const yearMonths = range(0, 12).map(_ => endOfMonth(addMonths(startDate, _)));
  const budgetContainers = useCollectionSubscription(company.ref.collection('budgetContainers').orderBy('index'));
  const customSections = useCollectionSubscription(company.ref.collection('customSections'));
  const customSegment1s = useCollectionSubscription(company.ref.collection('customSegment1s'));
  const customSegment2s = useCollectionSubscription(company.ref.collection('customSegment2s'));
  const customSegment3s = useCollectionSubscription(company.ref.collection('customSegment3s'));
  const budgetSubjectsByName = {
    section: keyBy(customSections, 'name'),
    segment1: keyBy(customSegment1s, 'name'),
    segment2: keyBy(customSegment2s, 'name'),
    segment3: keyBy(customSegment3s, 'name'),
  };
  const budgetSubjectsById = {
    section: keyBy(customSections, 'id'),
    segment1: keyBy(customSegment1s, 'id'),
    segment2: keyBy(customSegment2s, 'id'),
    segment3: keyBy(customSegment3s, 'id'),
  };
  const [isDragging, toggleDragging] = useToggle(false);
  const [isImporting, toggleImporting] = useToggle(false);
  const [showsModal, toggleModal] = useToggle(false);
  const sampleRows = () => {
    return [mapKeysToJa({
      budgetSubjectType: '',
      budgetSubjectName: '',
      accountItemName: '',
      dimensionType: '',
      subItemName: '',
      ...fromPairs(yearMonths.map(_ => ['_' + formatDate(_, 'YYYYMM'), ''])),
    })];
  };
  const onDrop = async ({ addedIndex, removedIndex }) => {
    const newIds = arrayMove(budgetContainers, removedIndex, addedIndex).map(_ => _.id);
    await batch(db, newIds, (batch, id, index) => {
      batch.update(company.ref.collection('budgetContainers').doc(id), { index });
    });
  };
  const budgetSubjectNames = [...customSections, ...customSegment1s, ...customSegment2s, ...customSegment3s].map(_ => _.name);
  const fieldsToImport = {
    budgetSubjectName: {
      validations: {
        exists: _ => budgetSubjectNames.includes(_),
      },
    },
    accountItemName: {
      validations: {
        exists: _ => accountItemNames.includes(_),
      },
    },
  };
  // 見込は手入力カスタム科目も指定できる。ただしカスタム科目は部門×月の値しか持てないため明細は指定できない
  const forecastFieldsToImport = {
    ...fieldsToImport,
    accountItemName: {
      validations: {
        exists: _ => accountItemNames.includes(_) || !!manualCustomAccountItemsByName[_],
      },
    },
    dimensionType: {
      validations: {
        customAccountItemMustBeNone: (v, row) => !manualCustomAccountItemsByName[row.accountItemName] || isEmpty(v) || v === allDimensions.none,
      },
    },
  };

  const rowsForExportOfContainer = async (containerId) => {
    const budgets = await getCollectionData(company.ref.collection('budgets').where('budgetContainerId', '==', containerId).where('period', '==', period));
    const budgetsGroupedBySubjectType = groupBy(budgets, 'budgetSubjectType');
    return entries(budgetSubjectTypes).flatMap(([budgetSubjectType, { label: budgetSubjectTypeLabel, }]) => {
      const budgets = budgetsGroupedBySubjectType[budgetSubjectType] || [];
      return entries(groupBy(budgets, 'budgetSubjectId')).flatMap(([budgetSubjectId, budgets]) => {
        const budgetsGroupedByAccountItemName = groupBy(budgets, 'accountItemName');
        return accountItems.flatMap((accountItem) => {
          const budgets = budgetsGroupedByAccountItemName[accountItem.name] || [];
          const budgetsGroupedByDimension = groupBy(budgets, 'dimension');
          return entries(allDimensions).flatMap(([dimension, dimensionLabel]) => {
            const budgets = budgetsGroupedByDimension[dimension] || [];
            return entries(groupBy(budgets, 'subItemName')).flatMap(([subItemName, budgets]) => {
              const budgetsGroupedBySameIndex = groupBy(budgets, _ => _.sameIndex || 0);
              return entries(budgetsGroupedBySameIndex).flatMap(([sameIndex, budgets]) => {
                const budgetsByYearMonth = keyBy(budgets, _ => _.closingDate.replace('-', '').slice(0, 6));
                return mapKeysToJa({
                  budgetSubjectType: budgetSubjectTypeLabel,
                  budgetSubjectName: budgetSubjectsById[budgetSubjectType][budgetSubjectId]?.name,
                  accountItemName: accountItem.name,
                  dimensionType: dimensionLabel,
                  subItemName,
                  ...fromPairs(yearMonths.map(_ => ['_' + formatDate(_, 'YYYYMM'), budgetsByYearMonth[formatDate(_, 'YYYYMM')]?.amount])),
                });
              });
            });
          });
        });
      });
    });
  };
  // 見込エクスポート: 通常科目に加え、手入力カスタム科目の _forecast 系列も行として出す
  const rowsForExportOfForecast = async () => {
    const budgetRows = await rowsForExportOfContainer('forecast');
    const customAccountItemRows = manualCustomAccountItems.flatMap((customAccountItem) => {
      const values = customAccountItem.occurrences || {};
      return entries(budgetSubjectTypes).flatMap(([budgetSubjectType, { label: budgetSubjectTypeLabel, }]) => {
        return Object.values(budgetSubjectsById[budgetSubjectType] || {}).flatMap((budgetSubject) => {
          const amountsByYearMonth = fromPairs(yearMonths.map((yearMonth) => {
            const key = [formatDate(yearMonth, 'YYYY/MM'), budgetSubject.id, manualValueSuffixes.forecast].join('');
            return ['_' + formatDate(yearMonth, 'YYYYMM'), values[key]];
          }));
          // NOTE: 既存の予算エクスポートと同様、値が入っている組み合わせのみ出力する
          if(Object.values(amountsByYearMonth).every(_ => _ == null)) return [];

          return [mapKeysToJa({
            budgetSubjectType: budgetSubjectTypeLabel,
            budgetSubjectName: budgetSubject.name,
            accountItemName: customAccountItem.name,
            dimensionType: allDimensions.none,
            subItemName: '',
            ...amountsByYearMonth,
          })];
        });
      });
    });
    return [...budgetRows, ...customAccountItemRows];
  };
  const processRows = (rows) => {
    return rows.map(_ => mapKeysFromJa(_, { 勘定科目: 'accountItemName', 金額: 'amount', })).filter(r => ['budgetSubjectType', 'budgetSubjectName', 'accountItemName'].every(_ => r[_]));
  };
  const saveRowsOfContainer = async (containerId, processedRows) => {
    const data = processedRows.flatMap((row) => {
      const { accountItemName, subItemName } = row;
      // NOTE: 手入力カスタム科目はbudgetsではなくカスタム科目ドキュメント側に保存する
      // NOTE: flatMapのコールバックがundefinedを返すと要素として残ってしまうため、空配列を返す
      if(manualCustomAccountItemsByName[accountItemName]) return [];

      const budgetSubjectType = findKey(budgetSubjectTypes, _ => _.label === row.budgetSubjectType);
      const budgetSubject = budgetSubjectsByName[budgetSubjectType][row.budgetSubjectName];
      const dimension = findKey(allDimensions, _ => _ === row.dimensionType) || 'none';
      if(dimension === 'none' && !isEmpty(subItemName)) return [];

      return entries(row).filter(_ => _[0].match(/_\d+/)).map(([yearMonth, amountString]) => {
        const itemKey = [accountItemName, 'none'].join('__');
        const [, year, month] = yearMonth.match(/_(\d{4})(\d{2})/);
        const closingDate = formatDate(endOfMonth(new Date(parseInt(year, 10), parseInt(month - 1, 10))), 'YYYY-MM-DD');
        const amount = parseFloat((amountString || '').replace(/,/g, ''), 10) || 0;
        const id = [containerId, budgetSubjectType, budgetSubject.id, accountItemName.replace(/\//g, '_'), dimension, subItemName.replace(/\//g, '_'), closingDate].join('__');
        return {
          id,
          itemKey,
          period,
          budgetContainerId: containerId,
          budgetSubjectType,
          budgetSubjectId: budgetSubject.id,
          accountItemName,
          dimension,
          subItemName,
          closingDate,
          amount,
        };
      });
    });
    const indexedData = Object.values(groupBy(data, 'id')).flatMap((data) => {
      return data.map((row, i) => {
        return {
          ...row,
          sameIndex: i,
          id: i === 0 ? row.id : [row.id, i].join('__'),
        };
      });
    });
    await batch(db, indexedData, (b, _) => b.set(company.ref.collection('budgets').doc(_.id), omit(_, 'id')));
    await onChangeBudgets?.();
  };
  // 見込CSVのうち手入力カスタム科目の行を、カスタム科目ドキュメントの _forecast 系列として保存する
  const saveManualForecastRowsOfCustomAccountItems = async (processedRows) => {
    const rowsGroupedByAccountItemName = groupBy(
      processedRows.filter(_ => manualCustomAccountItemsByName[_.accountItemName]),
      'accountItemName',
    );
    await entries(rowsGroupedByAccountItemName).reduce(async (x, [accountItemName, rows]) => {
      await x;

      const customAccountItem = manualCustomAccountItemsByName[accountItemName];
      const addedMonthValues = rows.reduce((x, row) => {
        const budgetSubjectType = findKey(budgetSubjectTypes, _ => _.label === row.budgetSubjectType);
        const budgetSubject = budgetSubjectsByName[budgetSubjectType]?.[row.budgetSubjectName];
        if(!budgetSubject) return x;

        return entries(row).filter(_ => _[0].match(/_\d+/)).reduce((x, [yearMonth, amountString]) => {
          const [, year, month] = yearMonth.match(/_(\d{4})(\d{2})/);
          const monthString = formatDate(new Date(parseInt(year, 10), parseInt(month - 1, 10)), 'YYYY/MM');
          const amount = parseFloat((amountString || '').replace(/,/g, ''), 10) || 0;
          return { ...x, [[monthString, budgetSubject.id, manualValueSuffixes.forecast].join('')]: amount };
        }, x);
      }, {});
      if(isEmpty(addedMonthValues)) return;

      await saveManualForecastValues(customAccountItem, addedMonthValues, yearMonths);
    }, Promise.resolve());
  };
  const saveForecastRows = async (processedRows) => {
    // NOTE: saveRowsOfContainer は末尾で onChangeBudgets を呼んで画面を再取得させるため、
    // カスタム科目側の保存を先に済ませてから呼ぶ
    await saveManualForecastRowsOfCustomAccountItems(processedRows);
    await saveRowsOfContainer('forecast', processedRows);
  };
  // 見込クリア時は、カスタム科目ドキュメント側の当年度の _forecast 値も0にする
  const clearManualForecastValuesOfCustomAccountItems = async () => {
    const budgetSubjects = [...customSections, ...customSegment1s, ...customSegment2s, ...customSegment3s];
    await manualCustomAccountItems.reduce(async (x, customAccountItem) => {
      await x;

      const clearedMonthValues = fromPairs(yearMonths.flatMap((yearMonth) => {
        return budgetSubjects.map(_ => [[formatDate(yearMonth, 'YYYY/MM'), _.id, manualValueSuffixes.forecast].join(''), 0]);
      }));
      await saveManualForecastValues(customAccountItem, clearedMonthValues, yearMonths);
    }, Promise.resolve());
  };
  const clearOfContainer = async (containerId) => {
    if(!window.confirm('選択されている年度のデータをクリアします。よろしいですか？')) return;
    try {
      const budgets = await getCollectionData(company.ref.collection('budgets').where('budgetContainerId', '==', containerId).where('period', '==', period));
      await batch(db, budgets, (b, _) => b.delete(_.ref));
      if(containerId === 'forecast') await clearManualForecastValuesOfCustomAccountItems();
      await onChangeBudgets?.();
      toast.success('クリアしました');
    } catch(e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };

  return (
    <Fragment>
      <Button onClick={toggleModal.bind(null, true)}>
        予算
      </Button>
      {
        showsModal && (
          <Modal isOpen style={{ minWidth: 900 }}>
            <ModalHeader>
              予算
            </ModalHeader>
            <ModalBody>
              {
                budgetContainers.length >= maxContainersCount && (
                  <div className="alert alert-warning">
                    予算数の上限に達しています。
                  </div>
                )
              }
              <div className="d-flex justify-content-end align-items-end gap-1">
                <ExportButton fileName="予算フォーマット.csv" label="予算フォーマット" rows={sampleRows} />
                <AddButton itemRef={company.ref.collection('budgetContainers').doc()} FormModal={ModelFormModal} formProps={{ title: '予算 追加', fields: fields(), }} processValues={_ => ({ ..._, index: (last(budgetContainers)?.index ?? 0) + 1 })} disabled={budgetContainers.length >= maxContainersCount} />
              </div>
              <div className="mt-3">
                <table className="table table-hover">
                  <thead className="thead-light">
                    <tr>
                      <th></th>
                      <th style={{ minWidth: 200 }}>予算名</th>
                      <th></th>
                    </tr>
                  </thead>
                  <Container
                    dragHandleSelector=".drag-handle"
                    onDrop={onDrop}
                    onDragStart={_ => toggleDragging(true)}
                    onDragEnd={_ => toggleDragging(false)}
                    dropPlaceholder={{ style: { background: 'eee', } }}
                    render={(ref) => (
                      <tbody ref={ref}>
                        {
                          budgetContainers.map((budgetContainer) => {
                            const { id, ref, name, } = budgetContainer;
                            const deleteItems = async () => {
                              const budgets = await getAllCollectionDataByChunk(company.ref.collection('budgets').where('budgetContainerId', '==', id));
                              await batch(db, budgets, (b, _) => b.delete(_.ref));
                              await ref.delete();
                              await onChangeBudgets?.();
                            };
                            const rowsForExport = () => rowsForExportOfContainer(id);
                            const saveRows = (processedRows) => saveRowsOfContainer(id, processedRows);
                            const saveOldRows = async (processedRows) => {
                              const data = processedRows.flatMap((row) => {
                                const { accountItemName, subItemName, closingDate, } = row;
                                const _closingDate = formatDate(new Date(closingDate), 'YYYY-MM-DD');
                                const amount = parseFloat((row.amount || '').replace(/,/g, ''), 10) || 0;
                                const budgetSubjectType = findKey(budgetSubjectTypes, _ => _.label === row.budgetSubjectType);
                                const budgetSubject = budgetSubjectsByName[budgetSubjectType][row.budgetSubjectName];
                                const dimension = findKey(allDimensions, _ => _ === row.dimensionType) || 'none';
                                if(dimension === 'none' && !isEmpty(subItemName)) return;

                                const itemKey = [accountItemName, 'none'].join('__');
                                const id = [budgetContainer.id, budgetSubjectType, budgetSubject.id, accountItemName.replace(/\//g, '_'), dimension, subItemName.replace(/\//g, '_'), _closingDate].join('__');
                                return {
                                  id,
                                  itemKey,
                                  period,
                                  budgetContainerId: budgetContainer.id,
                                  budgetSubjectType,
                                  budgetSubjectId: budgetSubject.id,
                                  accountItemName,
                                  dimension,
                                  subItemName,
                                  closingDate: _closingDate,
                                  amount,
                                };
                              });
                              const indexedData = Object.values(groupBy(data, 'id')).flatMap((data) => {
                                return data.map((row, i) => {
                                  return {
                                    ...row,
                                    sameIndex: i,
                                    id: i === 0 ? row.id : [row.id, i].join('__'),
                                  };
                                });
                              });
                              await batch(db, indexedData, (b, _) => b.set(company.ref.collection('budgets').doc(_.id), omit(_, 'id')));
                            };
                            const onClickClear = () => clearOfContainer(id);

                            return (
                              <Draggable
                                key={id}
                                render={() => (
                                  <tr style={{ display: !isDragging && 'table-row' }}>
                                    <td style={{ width: 30 }}>
                                      <div className="drag-handle text-muted cursor-pointer">
                                        <span className="fas fa-grip-vertical" />
                                      </div>
                                    </td>
                                    <td>
                                      {name}
                                    </td>
                                    <td className="text-right">
                                      <div className="d-flex gap-1 align-items-center justify-content-end">
                                        <ExportButton label={false} fileName={`${name}.csv`} rows={rowsForExport} />
                                        <ImportButton batchSize={30} label={false} beforeSave={saveRows} processRows={processRows} fields={fieldsToImport} />
                                        {user?.admin && <ImportButton label="旧予算" beforeSave={saveOldRows} processRows={processRows} fields={fieldsToImport} />}
                                        <ProgressButton process={onClickClear} color="danger" outline>
                                          <span className="fas fa-times" />
                                        </ProgressButton>
                                        <EditButton itemRef={ref} FormModal={ModelFormModal} formProps={{ title: '予算 編集', fields, }} />
                                        <DeleteButton deleteItems={deleteItems} />
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              />
                            );
                          })
                        }
                      </tbody>
                    )}
                  />
                </table>
              </div>
              {
                canUseForecast(company) && company.useForecast && (
                  <div className="mt-4">
                    <h5>見込</h5>
                    <table className="table table-hover">
                      <thead className="thead-light">
                        <tr>
                          <th style={{ minWidth: 200 }}>名称</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>見込</td>
                          <td className="text-right">
                            <div className="d-flex gap-1 align-items-center justify-content-end">
                              <ExportButton label={false} fileName="見込.csv" rows={rowsForExportOfForecast} />
                              <ImportButton batchSize={30} label={false} beforeSave={saveForecastRows} processRows={processRows} fields={forecastFieldsToImport} />
                              <ProgressButton process={() => clearOfContainer('forecast')} color="danger" outline>
                                <span className="fas fa-times" />
                              </ProgressButton>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )
              }
            </ModalBody>
            <ModalFooter>
              <Button className="cancel" color="secondary" onClick={toggleModal.bind(null, false)}>閉じる</Button>
            </ModalFooter>
          </Modal>
        )
      }
    </Fragment>
  );
}
